import { useLDClient, withLDProvider } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";

const DISABLE_LAUNCHDARKLY = process.env.VITE_DISABLE_LAUNCHDARKLY === "true";

/**
 * Wraps the root component with the LaunchDarkly provider or a no-op wrapper if launchdarkly is disabled.
 */
const withTWLaunchDarklyProvider = DISABLE_LAUNCHDARKLY
  ? <T extends {}>(Component: React.ComponentType<T>) => Component
  : withLDProvider({
      clientSideID: process.env.VITE_LAUNCHDARKLY_CLIENT_SIDE_ID || "",
    });

/**
 * Hook to identify the user with LaunchDarkly.
 */
const useLaunchDarkly = () => {
  const ldClient = useLDClient();

  useEffect(() => {
    if (DISABLE_LAUNCHDARKLY) {
      console.log("LaunchDarkly is disabled. Skipping identification.");
      return;
    }

    ldClient
      ?.identify({
        key: "anonymous",
        kind: "user",
      })
      .catch((error: unknown) => {
        console.error("Error identifying user with LaunchDarkly", error);
      });
  }, [ldClient]);
};

/**
 * Hook to get the value of a feature flag.
 */
const useFeatureFlag = (flag: string, defaultValue: boolean = false) => {
  const ldClient = useLDClient();

  if (DISABLE_LAUNCHDARKLY || !process.env.VITE_LAUNCHDARKLY_CLIENT_SIDE_ID) {
    console.log("LaunchDarkly is disabled. Returning default value for flag", flag, defaultValue);
    return defaultValue;
  }

  return ldClient?.variation(flag, defaultValue);
};

export { useFeatureFlag, useLaunchDarkly, withTWLaunchDarklyProvider };
